import {
  Booking,
  BookingQuestionData,
  BookingQuestionFileNameData,
  ModalDialog,
  NonModalDialogVariant,
  Organisation,
  RecurringBooking,
  Resource,
  SelectedPricePoint,
  Service,
  ServiceDuration,
  ServiceTier,
  TimeSlot,
  UserDetails,
} from "./types";

import { NotificationVariant } from "../common/components";

export enum ActionType {
  // Step navigation actions
  BACK_FROM_AVAILABILITY_STEP = "BACK_FROM_AVAILABILITY_STEP",
  BACK_FROM_BOOKING_QUESTIONS_STEP = "BACK_FROM_BOOKING_QUESTIONS_STEP",
  BACK_FROM_DETAILS_STEP = "BACK_FROM_DETAILS_STEP",
  BACK_FROM_DURATION_AND_ATTENDEES_STEP = "BACK_FROM_DURATION_AND_ATTENDEES_STEP",
  BACK_FROM_MULTI_TIER_STEP = "BACK_FROM_MULTI_TIER_STEP",
  BACK_FROM_PAYMENTS_STEP = "BACK_FROM_PAYMENTS_STEP",
  BACK_FROM_PRICE_POINTS_STEP = "BACK_FROM_PRICE_POINTS_STEP",
  BACK_FROM_RESOURCES_STEP = "BACK_FROM_RESOURCES_STEP",
  BACK_FROM_RECURRING_BOOKING_STEP = "BACK_FROM_RECURRING_BOOKING_STEP",
  BACK_FROM_SERVICES_STEP = "BACK_FROM_SERVICES_STEP",
  COMPLETE_AVAILABILITY_STEP = "COMPLETE_AVAILABILITY_STEP",
  COMPLETE_BOOKING_QUESTIONS_STEP = "COMPLETE_BOOKING_QUESTIONS_STEP",
  COMPLETE_DETAILS_STEP = "COMPLETE_DETAILS_STEP",
  COMPLETE_DURATION_AND_ATTENDEES_STEP = "COMPLETE_DURATION_AND_ATTENDEES_STEP",
  COMPLETE_LOCATION_AVAILABILITY_STEP = "COMPLETE_LOCATION_AVAILABILITY_STEP",
  COMPLETE_MULTI_TIER_STEP = "COMPLETE_MULTI_TIER_STEP",
  COMPLETE_ORGANISATION_STEP = "COMPLETE_ORGANISATION_STEP",
  COMPLETE_PAYMENTS_STEP = "COMPLETE_PAYMENTS_STEP",
  COMPLETE_PRICE_POINTS_STEP = "COMPLETE_PRICE_POINTS_STEP",
  COMPLETE_RECURRING_BOOKING_STEP = "COMPLETE_RECURRING_BOOKING_STEP",
  COMPLETE_RESOURCES_STEP = "COMPLETE_RESOURCES_STEP",
  COMPLETE_SERVICES_STEP = "COMPLETE_SERVICES_STEP",

  // Other actions
  APPLY_DISCOUNT_CODE = "APPLY_DISCOUNT_CODE",
  REMOVE_DISCOUNT_CODE = "REMOVE_DISCOUNT_CODE",

  CHANGE_SELECTED_TIMEZONE = "CHANGE_SELECTED_TIMEZONE",

  RESERVED_SPACE_DELETED = "RESERVED_SPACE_DELETED",

  RESTART_BOOKING_FLOW = "RESTART_BOOKING_FLOW",

  ADD_TOAST = "ADD_TOAST",
  REMOVE_TOAST = "REMOVE_TOAST",

  SHOW_NON_MODAL_DIALOG = "SHOW_NON_MODAL_DIALOG",
  HIDE_NON_MODAL_DIALOG = "HIDE_NON_MODAL_DIALOG",

  SHOW_MODAL_DIALOG = "SHOW_MODAL_DIALOG",
  HIDE_MODAL_DIALOG = "HIDE_MODAL_DIALOG",

  RESET_AFTER_RESERVATION_EXPIRY = "RESET_AFTER_RESERVATION_EXPIRY",
}

export type ReservationCreated = {
  attendees: number;
  duration: ServiceDuration;
  reservedSpaceId: string;
  resourceIds?: string[];
  selectedBookings?: Booking[];
  startDate: Date;
  timezone: string;
};

export type ResourceAfterAvailability = {
  attendees: number;
  duration: ServiceDuration;
  selectedTimeSlot: TimeSlot;
  timezone: string;
};

export type RecurringIntervalSelected = {
  attendees: number;
  duration: ServiceDuration;
  recurringBooking: RecurringBooking;
  timezone: string;
};

export type CompleteAvailabilityStepPayload = {
  updatedData:
    | RecurringIntervalSelected
    | ReservationCreated
    | ResourceAfterAvailability;
};

export type ReservationCreatedLocationAvailabilityStep = {
  attendees: number;
  duration: ServiceDuration;
  organisation: Organisation;
  reservedSpaceId: string;
  resource?: Resource;
  resourceIds?: string[];
  selectedBookings?: Booking[];
  service: Service;
  startDate: Date;
  timezone: string;
};

export type CompleteLocationAvailabilityStepPayload = {
  updatedData: ReservationCreatedLocationAvailabilityStep;
};

export type ChangeSelectedTimezonePayload = {
  updatedData: { timezone: string };
};

export type BackFromBookingQuestionsStepPayload = {
  updatedData: {
    bookingNotes?: string;
    bookingQuestionData?: BookingQuestionData;
  };
};
export type CompleteBookingQuestionsStepPayload = {
  updatedData: {
    bookingNotes?: string;
    bookingQuestionData?: BookingQuestionData;
    bookingQuestionFileNameData?: BookingQuestionFileNameData;
  };
};
export type BackFromDetailsStepPayload = {
  updatedData: { userDetails: UserDetails };
};
export type CompleteDetailsStepPayload = {
  updatedData: { confirmedBookingIds?: string[]; userDetails: UserDetails };
};

export type CompletePaymentStepPayload = {
  updatedData: {
    confirmedBookingIds?: string[];
    payNow?: boolean;
    totalPaid?: number;
  } | null;
};
export type CompleteOrganisationStepPayload = {
  updatedData: { organisation: Organisation };
};

type ReservationCreatedResourceStep = {
  attendees: number;
  duration: ServiceDuration;
  reservedSpaceId: string;
  resource?: Resource;
  resourceGroupId?: never;
  resourceGroupIds?: string[];
  resourceIds?: string[];
  selectedBookings?: Booking[];
  startDate: Date;
  timezone: string;
};

export type CompleteResourcesStepPayload = {
  updatedData:
    | ReservationCreatedResourceStep
    | { resource: Resource; resourceGroupId?: never }
    | { resource?: never; resourceGroupId: string }
    | null;
};
export type CompleteServicesStepPayload = {
  updatedData: { service: Service };
};
export type CompleteMultiTierStepPayload = {
  updatedData: { tier: ServiceTier };
};

export type CompletePricePointsStepPayload = {
  updatedData: { pricePoints: SelectedPricePoint[] };
};

export type ApplyDiscountCodePayload = {
  updatedData: { discountCode: string };
};

export type CompleteRecurringBookingStepPayload = {
  updatedData: {
    reservedSpaceId: string;
    selectedBookings: Booking[];
    startDate: Date;
  };
};

export type AddToastPayload = { message: string; variant: NotificationVariant };
export type RemoveToastPayload = { id: number };

export type ShowNonModalDialogPayload = { variant: NonModalDialogVariant };

export type ShowModalDialogPayload = ModalDialog;

type AvailabilityStepActions =
  | {
      payload: ChangeSelectedTimezonePayload;
      type: ActionType.CHANGE_SELECTED_TIMEZONE;
    }
  | {
      payload?: CompleteAvailabilityStepPayload;
      type: ActionType.COMPLETE_AVAILABILITY_STEP;
    }
  | { type: ActionType.BACK_FROM_AVAILABILITY_STEP };

type BookingQuestionsStepActions =
  | {
      payload: BackFromBookingQuestionsStepPayload;
      type: ActionType.BACK_FROM_BOOKING_QUESTIONS_STEP;
    }
  | {
      payload: CompleteBookingQuestionsStepPayload;
      type: ActionType.COMPLETE_BOOKING_QUESTIONS_STEP;
    };

type DetailsStepActions =
  | {
      payload: BackFromDetailsStepPayload;
      type: ActionType.BACK_FROM_DETAILS_STEP;
    }
  | {
      payload: CompleteDetailsStepPayload;
      type: ActionType.COMPLETE_DETAILS_STEP;
    };

type PaymentsStepActions =
  | {
      payload: CompletePaymentStepPayload;
      type: ActionType.COMPLETE_PAYMENTS_STEP;
    }
  | { payload: ApplyDiscountCodePayload; type: ActionType.APPLY_DISCOUNT_CODE }
  | { type: ActionType.BACK_FROM_PAYMENTS_STEP }
  | { type: ActionType.REMOVE_DISCOUNT_CODE };

type OrganisationStepActions = {
  payload: CompleteOrganisationStepPayload;
  type: ActionType.COMPLETE_ORGANISATION_STEP;
};

type ResourcesStepActions =
  | {
      payload: CompleteResourcesStepPayload;
      type: ActionType.COMPLETE_RESOURCES_STEP;
    }
  | { type: ActionType.BACK_FROM_RESOURCES_STEP };

type ServicesStepActions =
  | {
      payload: CompleteServicesStepPayload;
      type: ActionType.COMPLETE_SERVICES_STEP;
    }
  | { type: ActionType.BACK_FROM_SERVICES_STEP };

type RecurringBookingStepActions =
  | {
      payload?: CompleteRecurringBookingStepPayload;
      type: ActionType.COMPLETE_RECURRING_BOOKING_STEP;
    }
  | { type: ActionType.BACK_FROM_RECURRING_BOOKING_STEP };

type MultiTierStepActions =
  | {
      payload: CompleteMultiTierStepPayload;
      type: ActionType.COMPLETE_MULTI_TIER_STEP;
    }
  | { type: ActionType.BACK_FROM_MULTI_TIER_STEP };

type PricePointsStepActions =
  | {
      payload: CompletePricePointsStepPayload;
      type: ActionType.COMPLETE_PRICE_POINTS_STEP;
    }
  | { type: ActionType.BACK_FROM_PRICE_POINTS_STEP };

type RestartBookingFlowAction = {
  type: ActionType.RESTART_BOOKING_FLOW;
};

type ReservationActions =
  | { type: ActionType.RESERVED_SPACE_DELETED }
  | { type: ActionType.RESET_AFTER_RESERVATION_EXPIRY };

type ToastActions =
  | { payload: AddToastPayload; type: ActionType.ADD_TOAST }
  | { payload: RemoveToastPayload; type: ActionType.REMOVE_TOAST };

type NonModalDialogActions =
  | {
      payload: ShowNonModalDialogPayload;
      type: ActionType.SHOW_NON_MODAL_DIALOG;
    }
  | { type: ActionType.HIDE_NON_MODAL_DIALOG };

type ModalDialogActions =
  | {
      payload: ShowModalDialogPayload;
      type: ActionType.SHOW_MODAL_DIALOG;
    }
  | { type: ActionType.HIDE_MODAL_DIALOG };

type LocationAvailabilityStepActions = {
  payload: CompleteLocationAvailabilityStepPayload;
  type: ActionType.COMPLETE_LOCATION_AVAILABILITY_STEP;
};

export type Action =
  | AvailabilityStepActions
  | BookingQuestionsStepActions
  | DetailsStepActions
  | LocationAvailabilityStepActions
  | ModalDialogActions
  | MultiTierStepActions
  | NonModalDialogActions
  | OrganisationStepActions
  | PaymentsStepActions
  | PricePointsStepActions
  | RecurringBookingStepActions
  | ReservationActions
  | ResourcesStepActions
  | RestartBookingFlowAction
  | ServicesStepActions
  | ToastActions;
